import AnimatedIndicator from '@/components/AnimatedIndicator/AnimatedIndicator'
import AnimatedScore from '@/components/AnimatedScore/AnimatedScore'
import Team from '@/components/Team/Team'
import Trapezoid from '@/components/Trapezoid/Trapezoid'
import { useTheme } from '@/contexts/ThemeContext'
import { useResolution } from '@/hooks/useResolution'
import { useTemplateAnimation } from '@/hooks/useTemplateAnimation'
import { getHomeCompetitor, getAwayCompetitor } from '@/services/jsonDataService'
import { hexToRgba } from '@/utils/colorUtils'
import { POSITION_CLASSES, POSITION_ANIMATIONS } from '@/utils/scorebugPosition'
import './VolleyballBug.css'

import type { JsonMatchData, VolleyballScore, ScoreBugPosition } from '@/services/jsonDataService'
import type { MatchData } from '@/types'
import type { FC } from 'react'

export interface VolleyballBugProps {
  matchData?: MatchData
  jsonData: JsonMatchData | null
  isVisible?: boolean
  scorebugPosition?: ScoreBugPosition
}

const MULTIPLIERS = {
  teamSize: 20,
  scoreBoxSize: 2,
  scoreFontSize: 0.9,
  setsFontSize: 0.8,
  columnsSize: 4,
  columnsFontSize: 0.4,
  columnsLeadSize: 0.6,
  columnsLagSize: 0.3,
  indicatorColumnSize: 0.4,
}

const VolleyballBug: FC<VolleyballBugProps> = ({ jsonData, isVisible = true, scorebugPosition = 'bottom-left' }) => {
  const theme = useTheme()
  const resolution = useResolution()
  const { in: animationType } = POSITION_ANIMATIONS[scorebugPosition]
  const positionClass = POSITION_CLASSES[scorebugPosition]
  const animationRef = useTemplateAnimation(isVisible, { animationType, duration: 0.5 })

  // Get home and away competitors
  const homeCompetitor = getHomeCompetitor(jsonData)
  const awayCompetitor = getAwayCompetitor(jsonData)

  // Volleyball-specific score data
  const homeScore = homeCompetitor?.score as VolleyballScore | undefined
  const awayScore = awayCompetitor?.score as VolleyballScore | undefined

  const homeSetCount = homeScore?.sets ?? 0
  const awaySetCount = awayScore?.sets ?? 0
  const homePoints = homeScore?.points ?? 0
  const awayPoints = awayScore?.points ?? 0
  const homeIsServing = homeScore?.isServing ?? false
  const awayIsServing = awayScore?.isServing ?? false

  return (
    <div className={`volleyball-bug-content-wrapper ${positionClass}`}>
      <div
        ref={animationRef}
        className="volleyball-bug-panel"
        data-scorebug-panel
        style={{
          width: `calc(${resolution.height * 0.5} * (${theme.baseHeight} / 3.75)}`,
        }}
      >
        {/* Header row with column titles */}
        <div className="volleyball-bug-header">
          <Trapezoid
            variant="left"
            style={{
              background: hexToRgba(theme.colors.primary, 0.95),
              width: `calc(${theme.baseHeight} * ${MULTIPLIERS.columnsSize + MULTIPLIERS.indicatorColumnSize + MULTIPLIERS.columnsLeadSize + MULTIPLIERS.columnsLagSize})`,
              paddingLeft: `calc(${theme.baseHeight} * ${MULTIPLIERS.columnsLeadSize})`,
              paddingRight: `calc(${theme.baseHeight} * ${MULTIPLIERS.columnsLagSize})`,
              height: `calc(${theme.baseHeight} * 0.8)`,
            }}
          >
            <span
              className="volleyball-bug-column-title"
              style={{
                width: `calc(${theme.baseHeight} * ${MULTIPLIERS.scoreBoxSize})`,
                fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.columnsFontSize})`,
              }}
            >
              Sets
            </span>
            <span
              className="volleyball-bug-column-title"
              style={{
                width: `calc(${theme.baseHeight} * ${MULTIPLIERS.scoreBoxSize})`,
                fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.columnsFontSize})`,
              }}
            >
              Pts
            </span>
            <span
              style={{
                width: `calc(${theme.baseHeight} * ${MULTIPLIERS.indicatorColumnSize})`,
              }}
            />
          </Trapezoid>
        </div>

        {/* Home team row */}
        <div
          className="volleyball-bug-teams"
          style={{
            paddingRight: `calc(${theme.baseHeight} * ${MULTIPLIERS.columnsLagSize})`,
            background: hexToRgba(theme.colors.primary, 0.95),
          }}
        >
          <div className="volleyball-bug-team-row">
            <Team data={homeCompetitor} isHome style={{ flex: 1, minWidth: `${MULTIPLIERS.teamSize}rem` }} />
            <div className="volleyball-bug-scores-column">
              <div
                className="volleyball-bug-score-box"
                style={{ width: `calc(${theme.baseHeight} * ${MULTIPLIERS.scoreBoxSize})` }}
              >
                <AnimatedScore
                  value={homeSetCount}
                  className="volleyball-bug-score-value volleyball-bug-score-sets-value"
                  style={{ fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.setsFontSize})` }}
                />
              </div>
              <div
                className="volleyball-bug-score-box"
                style={{ width: `calc(${theme.baseHeight} * ${MULTIPLIERS.scoreBoxSize})` }}
              >
                <AnimatedScore
                  value={homePoints}
                  className="volleyball-bug-score-value"
                  style={{ fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.scoreFontSize})` }}
                />
              </div>
            </div>
            <div
              className="volleyball-bug-indicator-column"
              style={{ width: `calc(${theme.baseHeight} * ${MULTIPLIERS.indicatorColumnSize})` }}
            >
              <AnimatedIndicator
                isActive={homeIsServing}
                className="volleyball-bug-serve-indicator"
                style={{
                  width: `calc(${theme.baseHeight} * 0.4)`,
                  height: `calc(${theme.baseHeight} * 0.4)`,
                }}
              />
            </div>
          </div>

          {/* Away team row */}
          <div className="volleyball-bug-team-row">
            <Team data={awayCompetitor} style={{ flex: 1, minWidth: `${MULTIPLIERS.teamSize}rem` }} />
            <div className="volleyball-bug-scores-column">
              <div
                className="volleyball-bug-score-box"
                style={{ width: `calc(${theme.baseHeight} * ${MULTIPLIERS.scoreBoxSize})` }}
              >
                <AnimatedScore
                  value={awaySetCount}
                  className="volleyball-bug-score-value volleyball-bug-score-sets-value"
                  style={{ fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.setsFontSize})` }}
                />
              </div>
              <div
                className="volleyball-bug-score-box"
                style={{ width: `calc(${theme.baseHeight} * ${MULTIPLIERS.scoreBoxSize})` }}
              >
                <AnimatedScore
                  value={awayPoints}
                  className="volleyball-bug-score-value"
                  style={{ fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.scoreFontSize})` }}
                />
              </div>
            </div>
            <div
              className="volleyball-bug-indicator-column"
              style={{ width: `calc(${theme.baseHeight} * ${MULTIPLIERS.indicatorColumnSize})` }}
            >
              <AnimatedIndicator
                isActive={awayIsServing}
                className="volleyball-bug-serve-indicator"
                style={{
                  width: `calc(${theme.baseHeight} * 0.4)`,
                  height: `calc(${theme.baseHeight} * 0.4)`,
                }}
              />
            </div>
          </div>
        </div>

        {/* Footer bar */}
        <div
          className="volleyball-bug-footer"
          style={{
            background: hexToRgba(theme.colors.secondary, 0.95),
            height: `calc(${theme.baseHeight} * 0.8)`,
          }}
        >
          <img
            src="/assets/frogbox-logo-mono.png"
            alt="FrogBox"
            className="volleyball-bug-branding-logo"
            style={{ height: `calc(${theme.baseHeight} * 0.4)` }}
          />
        </div>
      </div>
    </div>
  )
}

export default VolleyballBug
